/**
 * Interactive decision buttons for AI chat.
 *
 * Renders a confirmation prompt with action buttons in a contained card.
 * The first option is styled as the primary action, others as outline.
 * After selection, the chosen button stays highlighted and others fade.
 */
import { useCallback } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';

interface DecisionButtonsProps {
  question: string;
  options: string[];
  /** Index of the selected option */
  selectedIndex?: number;
  /** Whether the buttons are interactive (clickable) */
  interactive: boolean;
  /** Called when user clicks a button (text + option index) */
  onSelect: (text: string, optionIndex: number) => void;
}

export function DecisionButtons({
  question,
  options,
  selectedIndex,
  interactive,
  onSelect,
}: DecisionButtonsProps) {
  const hasSelection = selectedIndex !== undefined;

  const handleClick = useCallback(
    (index: number) => {
      if (!interactive || hasSelection) return;
      onSelect(options[index], index);
    },
    [interactive, hasSelection, onSelect, options],
  );

  return (
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      <div className="px-4 pt-3 pb-2">
        <p className="text-sm font-semibold">{question}</p>
      </div>

      <div className="px-3 pb-3 flex flex-wrap gap-2">
        {options.map((option, index) => {
          const isSelected = selectedIndex === index;
          const isDisabled = !interactive || (hasSelection && !isSelected);
          const isPrimary = index === 0;

          return (
            <Button
              key={index}
              type="button"
              size="sm"
              variant={isPrimary && !isSelected ? 'default' : 'outline'}
              disabled={isDisabled}
              onClick={() => handleClick(index)}
              className={cn(
                'transition-all h-auto py-2 whitespace-normal text-left',
                isSelected && 'border-primary bg-primary/10 text-primary',
                isDisabled && !isSelected && 'opacity-40',
              )}
            >
              {isSelected && <Check className="h-3.5 w-3.5 mr-1.5 shrink-0" />}
              {option}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
